import { useEffect, useState, useContext } from 'react';
import { initializeApp } from 'firebase/app';
import { onAuthStateChanged } from 'firebase/auth';
import { LocalContext } from 'context';
import {
  UserProfileFactory,
  QuestionsFactory,
  ModeratorFactory,
  MiscFactory,
  EmailsFactory,
  AdminFactory,
  PresenceFactory,
  DeviceTrackingFactory,
  AnalyticsFactory,
  StorageFactory,
  SessionDocumentsFactory
} from 'firebase';
import getFirebaseInstance from './firebase';
import firebaseConfig from './config';

function useAuth() {
  const { browserTabIsActive } = useContext(LocalContext);
  const [user, setUser] = useState(null);
  const [firebase, setFirebase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userAuthInfo, setUserAuthInfo] = useState(null);

  useEffect(() => {
    const app = initializeApp(firebaseConfig);
    const firebaseInstance = getFirebaseInstance(app);

    firebaseInstance.userProfile = UserProfileFactory(firebaseInstance);
    firebaseInstance.questions = QuestionsFactory(firebaseInstance);
    firebaseInstance.moderator = ModeratorFactory(firebaseInstance);
    firebaseInstance.misc = MiscFactory(firebaseInstance);
    firebaseInstance.emails = EmailsFactory(firebaseInstance);
    firebaseInstance.admin = AdminFactory(firebaseInstance);
    firebaseInstance.presence = PresenceFactory(firebaseInstance);
    firebaseInstance.deviceTracking = DeviceTrackingFactory(firebaseInstance);
    firebaseInstance.analytics = AnalyticsFactory(firebaseInstance);
    firebaseInstance.storageFactory = StorageFactory(firebaseInstance);
    firebaseInstance.sessionDocuments =
      SessionDocumentsFactory(firebaseInstance);

    setFirebase(firebaseInstance);

    const unsubscribeFromFirebaseAuthStateChanges = onAuthStateChanged(
      firebaseInstance.auth,
      (_userAuthInfo) => {
        if (_userAuthInfo) {
          setUserAuthInfo(_userAuthInfo);
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    );

    return () => {
      if (unsubscribeFromFirebaseAuthStateChanges) {
        unsubscribeFromFirebaseAuthStateChanges();
      }
    };
  }, []);

  useEffect(() => {
    let unsubscribeFromUserUpdates;

    if (firebase && userAuthInfo && browserTabIsActive) {
      unsubscribeFromUserUpdates = firebase.subscribeToUserUpdates({
        uid: userAuthInfo.uid,
        snapshot: (_user) =>
          firebase.auth.currentUser
            .getIdTokenResult(true)
            .then(() => {
              if (_user.exists()) {
                const userDatabaseInfo = _user.data();
                setUser({
                  ...userAuthInfo,
                  ...userDatabaseInfo
                });
              }
            })
            .catch((error) => console.error(error.message))
            .finally(() => setLoading(false))
      });
    }
    return () => {
      if (unsubscribeFromUserUpdates) {
        unsubscribeFromUserUpdates();
      }
    };
    // We pass 'browserTabIsActive' into the dependency array so that if a user clicks on an old
    // tab after verifying in another tab then we can trigger all the above code again and get all
    // the 'user' doc data. If we didn't do this then the user's name would not show on the top
    // right and the 'Sessions' dropdown menu would not appear.
  }, [firebase, userAuthInfo, browserTabIsActive]);

  return { user, firebase, loading };
}

export default useAuth;
